import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Save, Send, CheckCircle, Upload, Workflow, ArrowDown } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { AdminHeader } from "@/components/admin/AdminShared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const CATS = ["Berita", "Kegiatan", "Pengumuman", "Pengawasan", "Kelembagaan", "Regulasi", "Informasi Publik", "Edukasi"];
const WORKFLOW = ["Unit Kerja", "Draft", "Verifikasi", "Review", "Approval", "Published"];
const IMG = "https://images.unsplash.com/photo-1554446422-d05db23719d2?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";

export default function NewsForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const editing = !!id;
  const [form, setForm] = useState({
    title: "", category: "Berita", author: "Humas Bawaslu Sultra", thumbnail: IMG,
    summary: "", content: "", tags: "", status: "Draft",
    source_unit: "Unit Kerja Humas", verifikator: "Divisi Hukum & Penindakan", reviewer: "Koordinator Humas",
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (editing) {
      api.get(`/news?admin=true&limit=100`).then((r) => {
        const n = r.data.items.find((x) => x.id === id);
        if (n) setForm({ ...n, tags: (n.tags || []).join(", ") });
      });
    }
  }, [id, editing]);

  const set = (k, v) => setForm({ ...form, [k]: v });

  const save = async (status) => {
    if (!form.title.trim() || !form.summary.trim() || !form.content.trim()) {
      toast.error("Judul, ringkasan, dan isi wajib diisi"); return;
    }
    setLoading(true);
    const payload = { ...form, status, tags: form.tags ? form.tags.split(",").map((t) => t.trim()).filter(Boolean) : [] };
    try {
      if (editing) await api.put(`/news/${id}`, payload);
      else await api.post("/news", payload);
      toast.success(`Berita disimpan (${status})`);
      navigate("/admin/news");
    } catch (e) {
      toast.error("Gagal menyimpan berita");
    } finally { setLoading(false); }
  };

  return (
    <div>
      <button onClick={() => navigate("/admin/news")} className="inline-flex items-center gap-2 text-sm font-semibold text-slate-500 hover:text-brandred mb-4">
        <ArrowLeft className="h-4 w-4" /> Kembali ke Daftar Berita
      </button>
      <AdminHeader title={editing ? "Edit Berita" : "Tambah Berita"} description="Lengkapi informasi berita sesuai alur verifikasi." />

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-white border border-slate-200 rounded-xl p-6 space-y-5" data-testid="news-form">
          <div>
            <Label>Judul *</Label>
            <Input value={form.title} onChange={(e) => set("title", e.target.value)} className="mt-1.5" data-testid="news-title-input" placeholder="Judul berita" />
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label>Kategori</Label>
              <Select value={form.category} onValueChange={(v) => set("category", v)}>
                <SelectTrigger className="mt-1.5" data-testid="news-category-select"><SelectValue /></SelectTrigger>
                <SelectContent>{CATS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Penulis</Label><Input value={form.author} onChange={(e) => set("author", e.target.value)} className="mt-1.5" /></div>
          </div>
          <div>
            <Label>URL Thumbnail</Label>
            <Input value={form.thumbnail} onChange={(e) => set("thumbnail", e.target.value)} className="mt-1.5" />
            {form.thumbnail && <img src={form.thumbnail} alt="preview" className="mt-2 h-32 rounded-lg object-cover" />}
          </div>
          <div><Label>Ringkasan *</Label><Textarea value={form.summary} onChange={(e) => set("summary", e.target.value)} rows={2} className="mt-1.5" data-testid="news-summary-input" /></div>
          <div><Label>Isi Berita * (mendukung HTML)</Label><Textarea value={form.content} onChange={(e) => set("content", e.target.value)} rows={8} className="mt-1.5" data-testid="news-content-input" placeholder="<p>Tulis isi berita...</p>" /></div>
          <div><Label>Tag (pisahkan dengan koma)</Label><Input value={form.tags} onChange={(e) => set("tags", e.target.value)} className="mt-1.5" placeholder="Pemilu, Pengawasan" /></div>
        </div>

        <div className="space-y-6">
          <div className="bg-white border border-slate-200 rounded-xl p-5">
            <h3 className="font-bold text-navy mb-3">Verifikasi Konten</h3>
            <div className="space-y-3 text-sm">
              <div><Label className="text-xs">Sumber / Unit Pengirim</Label><Input value={form.source_unit} onChange={(e) => set("source_unit", e.target.value)} className="mt-1 h-9" /></div>
              <div><Label className="text-xs">Verifikator</Label><Input value={form.verifikator} onChange={(e) => set("verifikator", e.target.value)} className="mt-1 h-9" /></div>
              <div><Label className="text-xs">Reviewer</Label><Input value={form.reviewer} onChange={(e) => set("reviewer", e.target.value)} className="mt-1 h-9" /></div>
              <div className="pt-1"><Label className="text-xs">Status Saat Ini</Label><div className="mt-1 font-bold text-brandred">{form.status}</div></div>
            </div>
          </div>

          <div className="bg-navy text-white rounded-xl p-5">
            <h3 className="font-bold flex items-center gap-2 mb-3 text-sm"><Workflow className="h-4 w-4 text-brandred" /> Alur Kerja</h3>
            <div className="flex flex-col">
              {WORKFLOW.map((w, i) => (
                <div key={w} className="flex flex-col">
                  <span className={`text-xs px-2.5 py-1.5 rounded ${form.status === w || (w === "Published" && form.status === "Published") ? "bg-brandred font-bold" : "bg-white/10"}`}>{w}</span>
                  {i < WORKFLOW.length - 1 && <ArrowDown className="h-3 w-3 text-slate-400 my-0.5 ml-3" />}
                </div>
              ))}
            </div>
          </div>

          <div className="bg-white border border-slate-200 rounded-xl p-5 space-y-2.5">
            <Button onClick={() => save("Draft")} disabled={loading} variant="outline" className="w-full justify-start" data-testid="save-draft"><Save className="h-4 w-4 mr-2" /> Simpan Draft</Button>
            <Button onClick={() => save("Review")} disabled={loading} variant="outline" className="w-full justify-start" data-testid="submit-review"><Send className="h-4 w-4 mr-2" /> Ajukan Review</Button>
            <Button onClick={() => save("Approved")} disabled={loading} variant="outline" className="w-full justify-start" data-testid="approve-news"><CheckCircle className="h-4 w-4 mr-2" /> Approve</Button>
            <Button onClick={() => save("Published")} disabled={loading} className="w-full justify-start bg-brandred hover:bg-brandred-700 text-white" data-testid="publish-news"><Upload className="h-4 w-4 mr-2" /> Publish</Button>
          </div>
        </div>
      </div>
    </div>
  );
}
